import React from 'react';
import styled from 'styled-components';
import { Button, Space, Tooltip } from 'antd';
import { AppstoreOutlined, BarsOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { PiCameraBold } from 'react-icons/pi';

import { useTranslation } from 'react-i18next';

interface FreePhotoHeaderProps {
  viewMode: 'grid' | 'list' | 'map';
  totalCount: number;
  onViewModeChange: (mode: 'grid' | 'list' | 'map') => void;
}

/**
 * Header component for FreePhoto screen with view mode toggle
 */
export const FreePhotoHeader: React.FC<FreePhotoHeaderProps> = ({ viewMode, totalCount, onViewModeChange }) => {
  const { t } = useTranslation();

  return (
    <UI_Wrapper>
      <UI_TitleRow>
        <UI_Title>
          <PiCameraBold style={{ fontSize: '18px', color: '#64748b' }} />
          <span>{t('free_photos_title')}</span>
        </UI_Title>
        {totalCount > 0 && <UI_Badge>{t('free_photos_photos_count', { count: totalCount })}</UI_Badge>}
      </UI_TitleRow>

      <UI_ViewModeToggle>
        <Space.Compact>
          <Tooltip title={t('free_photos_view_grid')}>
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => onViewModeChange('grid')}
            />
          </Tooltip>
          <Tooltip title={t('free_photos_view_list')}>
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<BarsOutlined />}
              onClick={() => onViewModeChange('list')}
            />
          </Tooltip>
          <Tooltip title={t('free_photos_view_map')}>
            <Button
              type={viewMode === 'map' ? 'primary' : 'default'}
              icon={<EnvironmentOutlined />}
              onClick={() => onViewModeChange('map')}
            />
          </Tooltip>
        </Space.Compact>
      </UI_ViewModeToggle>
    </UI_Wrapper>
  );
};

const UI_Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const UI_TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UI_Title = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UI_Badge = styled.span`
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const UI_ViewModeToggle = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
`;
